import React from 'react'
import { NavLink } from 'react-router-dom'

import { stringify } from 'querystring'

import {
  DocumentHead,
  DocumentHeader,
  ErrorMessage,
  Segment,
  handleFetchMore,
  useForTablet,
  useModalContext,
} from '@sketch/components'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { useQueryParams } from '@sketch/modules-common'

import {
  ProjectListItemFragment,
  ShareListItemFragment,
  WorkspaceMinimalFragment,
  useGetArchivedQuery,
  useGetWorkspaceUploadsRestrictedForQuery,
} from '@sketch/gql-types'

import LoadingState from '../LoadingState'
import DocumentItemsLayout from '../DocumentItemsLayout'
import ShareEmptyState from '../ShareEmptyState'
import { useSearch } from '../../hooks/useSearch'
import { DocumentItemDropdown } from '../DocumentItemDropdown'

import { Wrapper } from './ArchiveList.styles'
import ProjectItemsLayout from '../ProjectItemsLayout'
import ProjectDropdown from 'modules/projects/components/ProjectDropdown'
import { RenameProject } from 'modules/projects'

type SegmentType = 'DOCUMENT' | 'PROJECT'

const ENTRIES_PATH = ['workspace', 'archived', 'entries']
const SEGMENTS = ['DOCUMENT', 'PROJECT'] as SegmentType[]
const SEGMENTS_LABEL = {
  DOCUMENT: 'Documents',
  PROJECT: 'Projects',
}

const DEFAULT_SEGMENT = 'DOCUMENT'

interface ArchiveListProps {
  workspaceId: string
  workspace: WorkspaceMinimalFragment
  userRole: WorkspaceMinimalFragment['userRole']
}

export function ArchiveList({
  workspaceId,
  workspace,
  userRole,
}: ArchiveListProps) {
  const { searchDebounced: search } = useSearch()
  const isTabletAndBigger = useForTablet()

  const params = useQueryParams<'WORKSPACE_ARCHIVE'>()
  const { showModal } = useModalContext()

  const activeSegment = (SEGMENTS.includes(params.type || ('' as any))
    ? params.type!
    : DEFAULT_SEGMENT) as typeof SEGMENTS[number]

  const { loading, error, data, fetchMore } = useGetArchivedQuery({
    variables: { workspaceIdentifier: workspaceId, type: activeSegment },
  })

  const uploadsRestrictedForResult = useGetWorkspaceUploadsRestrictedForQuery({
    variables: { identifier: workspaceId },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.identifier !== curr?.identifier,
  })

  if (loading) {
    return <LoadingState />
  }

  if (!data || error) {
    return <ErrorMessage.Generic />
  }

  const totalCount = data?.workspace.archived.meta.totalCount || 0
  const documentHeader = isTabletAndBigger && (
    <>
      <DocumentHead title="Archive" />
      <DocumentHeader
        title="Archive"
        description="Use the archive to store documents that are no longer relevant or that you don’t access frequently."
        learnMoreTooltipTitle="Learn more about archive"
        learnMoreTooltipURL="https://shrug.com"
        actions={
          <Wrapper>
            {SEGMENTS.map(value => (
              <NavLink
                key={value}
                to={location => ({
                  ...location,
                  search: stringify({ ...params, type: value }),
                })}
              >
                <Segment
                  type="text"
                  label={SEGMENTS_LABEL[value]}
                  active={activeSegment === value}
                />
              </NavLink>
            ))}
          </Wrapper>
        }
      />
    </>
  )

  if (!loading && totalCount === 0) {
    const uploadsRestrictedFor =
      uploadsRestrictedForResult.data?.workspace?.uploadsRestrictedFor ??
      // we should never need to default back to 'NONE', as we just checked
      // that uploadsRestrictedForResult is not in a loading state anymore
      // So there should be a valid data, but just in case, default it back
      // to 'NONE' either way.
      'NONE'

    return (
      <>
        {documentHeader}

        <ShareEmptyState
          search={search}
          workspace={workspace}
          workspaceHasDocuments={!!totalCount}
          uploadsRestrictedFor={uploadsRestrictedFor}
          filter={
            activeSegment === 'DOCUMENT'
              ? 'ARCHIVE_DOCUMENTS'
              : 'ARCHIVE_PROJECTS'
          }
        />
      </>
    )
  }

  const { after } = data.workspace.archived.meta
  const loadMoreHandler = handleFetchMore(fetchMore, ENTRIES_PATH, {
    dataIdFromObject,
    after,
  })

  let content = null
  if (activeSegment === 'DOCUMENT') {
    const items = data.workspace.archived.entries.filter(
      item => item.__typename === 'Share'
    ) as ShareListItemFragment[]

    content = (
      <DocumentItemsLayout
        shares={items}
        search={search}
        onLoadMore={loadMoreHandler}
        totalCount={totalCount}
        showProjectName
        renderDropdown={share => (
          <DocumentItemDropdown
            share={share}
            workspaceIdentifier={workspaceId}
          />
        )}
      />
    )
  } else {
    const items = data.workspace.archived.entries.filter(
      item => item.__typename === 'Project'
    ) as ProjectListItemFragment[]

    content = (
      <ProjectItemsLayout
        projects={items}
        onLoadMore={loadMoreHandler}
        totalCount={totalCount}
        renderDropdown={project => (
          <ProjectDropdown
            workspace={workspace}
            project={project}
            onRename={() => {
              showModal(RenameProject, {
                workspaceId,
                projectId: project.identifier,
                projectName: project.name,
              })
            }}
          />
        )}
        workspaceId={workspaceId}
      />
    )
  }

  return (
    <>
      {documentHeader}
      {content}
    </>
  )
}
